import { useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { useOverlayLayer } from './overlayLayer';

export interface DrawerProps {
  open: boolean;
  onClose: () => void;
  /** Accessible name of the drawer's dialog. */
  label: string;
  /** Pinned above the scroller (the account header). */
  header?: ReactNode;
  children: ReactNode;
}

/**
 * The handheld navigation drawer (ADR 0048 §3.2).
 *
 * Left-anchored, 85 % of the viewport up to 20rem, full height, a scrim behind
 * it and one scroller of its own (`.drawer__scroll`). It closes on scrim tap,
 * Escape and — wired by the shell — on any navigation.
 *
 * Two things it deliberately does not do:
 * - **No phantom history entry.** Back closes the drawer (the owner lost
 *   their place to Back with it open), which needs an entry to pop; the
 *   entry is taken back off when the drawer is closed by a tap instead, so
 *   there is no double back (`useBackToClose`, via `useOverlayLayer`).
 * - **No edge swipe to open.** The left edge is the browser's back gesture on
 *   both mobile platforms.
 *
 * It is modal (`aria-modal`, focus trapped, document locked): while it is open
 * the page beside it is behind a scrim and cannot be used, so telling assistive
 * tech otherwise would be a lie.
 */
export function Drawer({ open, onClose, label, header, children }: DrawerProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  useOverlayLayer(panelRef, open, onClose);

  if (!open) return null;

  return createPortal(
    <div className="drawer">
      <button
        type="button"
        className="drawer__scrim"
        aria-label="Close navigation"
        tabIndex={-1}
        onClick={onClose}
      />
      <div
        ref={panelRef}
        className="drawer__panel"
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
      >
        {header && <div className="drawer__header">{header}</div>}
        <div className="drawer__scroll">{children}</div>
      </div>
    </div>,
    document.body,
  );
}
